// Jeden řádek seznamu zastávek — komponenta .stop z prototypu „Noční kokpit":
// vlevo pořadí+název+adresa, vpravo VŽDY okno nahoře a slovní stav dole
// (žádné holé číslo). Stavy: now (zlatý pruh), done (ustupuje), tsk (úkol).

import type { StopStatus } from "../store/stopProgressStore";

interface Props {
  idx: number | null;              // null = úkol (ikona místo čísla)
  name: string;
  address?: string;
  client?: string;
  branchId?: string;
  windowFrom?: string;
  windowTo?: string;
  stopStatus: StopStatus;
  /** Zvýraznění aktuální zastávky (první nevyřízená). */
  current?: boolean;
  /** Úkol (TRANK / materiál) — plný obrys, ikona, štítek „úkol". */
  task?: boolean;
  doneAt?: string; // HH:MM
  futileReason?: string;
  storno?: boolean;
  stornoAcked?: boolean;
  note?: string;
  temp?: boolean;
  /** Odhad příjezdu HH:MM (už posunutý o pauzu). */
  eta?: string;
  onClick: () => void;
}

const hhmmToMin = (v?: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((v ?? "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const FUTILE_LABELS: Record<string, string> = {
  closed: "zavřeno",
  "no-bag": "odvod nepřipraven",
  "no-one": "nikdo nebyl",
  other: "jiný důvod",
};

const CubeIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M21 8l-9-4.5L3 8v8l9 4.5L21 16V8z" />
    <path d="M3 8l9 4.5L21 8M12 12.5V20.5" />
  </svg>
);

export function StopRow({
  idx, name, address, client, branchId, windowFrom, windowTo,
  stopStatus, current, task, doneAt, futileReason, storno, stornoAcked,
  note, temp, eta, onClick,
}: Props) {
  const closed = stopStatus === "done" || stopStatus === "futile";
  const stornoAckedState = !!(storno && stornoAcked);
  const effectivelyClosed = closed || stornoAckedState;

  // Slovní stav vpravo dole — vždy slova, nikdy holé číslo.
  let stateEl: React.ReactNode;
  if (storno) {
    stateEl = <span className={stornoAckedState ? undefined : "st-bad"}>{stornoAckedState ? "storno · odklikáno" : "neprovádět"}</span>;
  } else if (stopStatus === "done") {
    stateEl = <span className="st-ok">{doneAt ? `dorazil ${doneAt}` : "hotovo"}</span>;
  } else if (stopStatus === "futile") {
    stateEl = <span className="st-warn">marná{futileReason ? ` · ${FUTILE_LABELS[futileReason] ?? futileReason}` : ""}</span>;
  } else if (stopStatus === "arrived") {
    stateEl = <span className="st-ok">na místě</span>;
  } else if (stopStatus === "started") {
    stateEl = <span className="st-warn">jedu tam</span>;
  } else if (eta) {
    const e = hhmmToMin(eta), b = hhmmToMin(windowTo);
    const late = e != null && b != null && e > b;
    stateEl = late
      ? <span className="st-bad">po okně ~{eta}</span>
      : <span>příjezd ~{eta}</span>;
  } else {
    stateEl = <span>čeká</span>;
  }

  const cls = [
    "stop",
    current ? "now" : "",
    effectivelyClosed ? "done" : "",
    task ? "tsk" : "",
  ].filter(Boolean).join(" ");

  return (
    <button
      className={cls}
      onClick={onClick}
      style={storno && !stornoAckedState ? { borderColor: "rgba(242,101,88,.5)", background: "var(--bad-dim)" } : undefined}
    >
      <span className="rail">
        {task ? <CubeIcon /> : effectivelyClosed ? (stopStatus === "futile" ? "✕" : "✓") : idx ?? ""}
      </span>
      <span className="main">
        <span className="nm">
          {branchId && <span className="bid">{branchId}</span>}
          {name.toUpperCase()}
          {task && <span className="chip-ukol">úkol</span>}
          {temp && <span className="chip-ukol" style={{ borderColor: "rgba(110,155,240,.0)", color: "var(--soft)" }}>dočasná</span>}
          {storno && !stornoAckedState && (
            <span className="chip-ukol" style={{ borderColor: "var(--bad)", color: "var(--bad)" }}>storno</span>
          )}
        </span>
        <span className="ad">{note ? `${address ?? client ?? ""} · ${note}` : address ?? client ?? "—"}</span>
      </span>
      <span className="win">
        {windowFrom && windowTo ? <b>{windowFrom}–{windowTo}</b> : task ? <span>po trase</span> : null}
        {stateEl}
      </span>
    </button>
  );
}
